export const SYNC_BATCH_BYTES = 256 * 1024;
export const SYNC_CHECKPOINT_BYTES = 1024 * 1024;
export const SYNC_TOTAL_BYTES = 2 * 1024 * 1024 * 1024;
export type SyncClock = Record<string, number>;
export type ChangeV2 = { key: string; value: unknown };
export type ConflictV2 = { key: string; versions: { operationId: string; value: unknown }[] };
export type OperationV2 = {
  format: 2;
  datasetId: string;
  generation: string;
  keyVersion: number;
  id: string;
  deviceId: string;
  sequence: number;
  context: SyncClock;
  changes: ChangeV2[];
  resolves?: ConflictV2;
  transaction?: { id: string; part: number; total: number };
};

export function canonicalSync(value: unknown): string {
  if (value === null || typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number" && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalSync).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonicalSync(v)}`)
      .join(",")}}`;
  throw new Error("sync-format");
}

export function syncId(value: unknown): value is string {
  return typeof value === "string" && /^[\w-]{1,128}$/.test(value);
}

export function assertOperation(input: unknown): asserts input is OperationV2 {
  const op = input as OperationV2;
  if (
    !op ||
    op.format !== 2 ||
    ![op.datasetId, op.generation, op.id, op.deviceId].every(syncId) ||
    !Number.isSafeInteger(op.keyVersion) ||
    op.keyVersion < 1 ||
    !Number.isSafeInteger(op.sequence) ||
    op.sequence < 1 ||
    !op.context ||
    typeof op.context !== "object" ||
    Array.isArray(op.context) ||
    Object.keys(op.context).length > 128 ||
    Object.entries(op.context).some(([k, v]) => !syncId(k) || !Number.isSafeInteger(v) || v < 0) ||
    (op.context[op.deviceId] ?? 0) !== op.sequence - 1 ||
    !Array.isArray(op.changes) ||
    !op.changes.length ||
    op.changes.length > 8192 ||
    op.changes.some(
      (c) =>
        !c ||
        typeof c.key !== "string" ||
        !c.key.length ||
        c.key.length > 2048 ||
        [...c.key].some((character) => character.charCodeAt(0) < 32),
    ) ||
    new Set(op.changes.map((c) => c.key)).size !== op.changes.length
  )
    throw new Error("sync-format");
  if (
    op.transaction &&
    (!syncId(op.transaction.id) ||
      !Number.isSafeInteger(op.transaction.part) ||
      !Number.isSafeInteger(op.transaction.total) ||
      op.transaction.total < 2 ||
      op.transaction.total > 4096 ||
      op.transaction.part < 0 ||
      op.transaction.part >= op.transaction.total)
  )
    throw new Error("sync-format");
  // Reserve room for the source signature and compression framing.
  if (new TextEncoder().encode(canonicalSync(op)).length > SYNC_BATCH_BYTES - 1024) throw new Error("sync-batch-limit");
}

export function operationBatches(
  scope: Pick<OperationV2, "datasetId" | "generation" | "deviceId" | "keyVersion">,
  clock: SyncClock,
  changes: ChangeV2[],
  randomId: () => string,
): OperationV2[] {
  const groups: ChangeV2[][] = [];
  let current: ChangeV2[] = [];
  let bytes = 0;
  const overhead = new TextEncoder().encode(canonicalSync(clock)).length + 4096;
  for (const change of changes) {
    const size = new TextEncoder().encode(canonicalSync(change)).length + 1;
    if (size + overhead > SYNC_BATCH_BYTES - 1024) throw new Error("sync-batch-limit");
    if (current.length && bytes + size + overhead > SYNC_BATCH_BYTES - 1024) {
      groups.push(current);
      current = [];
      bytes = 0;
    }
    current.push(change);
    bytes += size;
  }
  if (current.length) groups.push(current);
  const transactionId = randomId();
  const context = { ...clock };
  return groups.map((part, index) => {
    const sequence = (context[scope.deviceId] ?? 0) + 1;
    const op: OperationV2 = {
      format: 2,
      ...scope,
      id: randomId(),
      sequence,
      context: { ...context },
      changes: part,
      ...(groups.length > 1 ? { transaction: { id: transactionId, part: index, total: groups.length } } : {}),
    };
    assertOperation(op);
    context[scope.deviceId] = sequence;
    return op;
  });
}

export type VersionV2 = {
  key: string;
  operationId: string;
  deviceId: string;
  sequence: number;
  context: SyncClock;
  value: unknown;
};
export function mergeOperationVersions(current: VersionV2[], op: OperationV2): VersionV2[] {
  const changed = new Map(op.changes.map((c) => [c.key, c.value]));
  const next = current.filter((v) => !changed.has(v.key) || (op.context[v.deviceId] ?? 0) < v.sequence);
  for (const change of op.changes) {
    if (
      next.some(
        (v) => v.key === change.key && ((v.context[op.deviceId] ?? 0) >= op.sequence || v.operationId === op.id),
      )
    )
      continue;
    next.push({
      key: change.key,
      operationId: op.id,
      deviceId: op.deviceId,
      sequence: op.sequence,
      context: op.context,
      value: change.value,
    });
  }
  return next;
}
export function versionConflicts(versions: VersionV2[]): ConflictV2[] {
  const byKey = new Map<string, VersionV2[]>();
  for (const v of versions) {
    const items = byKey.get(v.key) ?? [];
    items.push(v);
    byKey.set(v.key, items);
  }
  return [...byKey]
    .filter(([, items]) => new Set(items.map((v) => canonicalSync(v.value))).size > 1)
    .map(([key, items]) => ({
      key,
      versions: items
        .map((v) => ({ operationId: v.operationId, value: v.value }))
        .sort((a, b) => (a.operationId < b.operationId ? -1 : a.operationId > b.operationId ? 1 : 0)),
    }));
}

// Version vectors avoid materialising the transitive ancestor set for every
// operation. Arrival order and device wall clocks never choose a winner.
export function projectOperations(input: OperationV2[], checkpointClock: SyncClock = {}) {
  const ops = new Map<string, OperationV2>();
  const sequences = new Map<string, Map<number, string>>();
  const clock: SyncClock = { ...checkpointClock };
  let scope: string | undefined;
  for (const op of input) {
    assertOperation(op);
    const nextScope = `${op.datasetId}/${op.generation}`;
    if (scope && scope !== nextScope) throw new Error("sync-generation-mismatch");
    scope = nextScope;
    const before = ops.get(op.id);
    if (before) {
      if (canonicalSync(before) !== canonicalSync(op)) throw new Error("sync-id-reused");
      continue;
    }
    const device = sequences.get(op.deviceId) ?? new Map<number, string>();
    if (device.has(op.sequence)) throw new Error("sync-device-fork");
    if (op.sequence <= (checkpointClock[op.deviceId] ?? 0)) throw new Error("sync-checkpoint-replay");
    device.set(op.sequence, op.id);
    sequences.set(op.deviceId, device);
    clock[op.deviceId] = Math.max(clock[op.deviceId] ?? 0, op.sequence);
    ops.set(op.id, op);
  }
  for (const [device, entries] of sequences) {
    const ordered = [...entries.keys()].sort((a, b) => a - b);
    if (ordered.some((n, i) => n !== (checkpointClock[device] ?? 0) + i + 1)) throw new Error("sync-missing-parent");
  }
  for (const op of ops.values()) {
    for (const [device, n] of Object.entries(op.context)) {
      if (n > (clock[device] ?? 0)) throw new Error("sync-missing-parent");
      const parent = ops.get(sequences.get(device)?.get(n) ?? "");
      if (parent && Object.entries(parent.context).some(([d, s]) => (op.context[d] ?? 0) < s))
        throw new Error("sync-invalid-causality");
    }
  }
  const versions = new Map<string, { op: OperationV2; value: unknown }[]>();
  for (const op of ops.values())
    for (const change of op.changes) {
      const items = versions.get(change.key) ?? [];
      items.push({ op, value: change.value });
      versions.set(change.key, items);
    }
  const values: ChangeV2[] = [];
  const conflicts: ConflictV2[] = [];
  for (const [key, items] of [...versions].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
    const current = items.filter(
      (item) => !items.some((other) => (other.op.context[item.op.deviceId] ?? 0) >= item.op.sequence),
    );
    const result = current
      .map((item) => ({ operationId: item.op.id, value: item.value }))
      .sort((a, b) => (a.operationId < b.operationId ? -1 : a.operationId > b.operationId ? 1 : 0));
    if (new Set(result.map((item) => canonicalSync(item.value))).size > 1) conflicts.push({ key, versions: result });
    else if (result[0]) values.push({ key, value: result[0].value });
  }
  return { values, conflicts, clock };
}

export function assertCurrentResolution(conflicts: ConflictV2[], decision: ConflictV2) {
  const current = conflicts.find((item) => item.key === decision.key);
  if (!current || canonicalSync(current) !== canonicalSync(decision)) throw new Error("sync-resolution-stale");
}
